/**
 * EditCampaignDialog - Wizard de edição de campanhas existentes
 * 
 * Similar ao CreateCampaignDialog, mas pré-carrega dados da campanha
 * e permite edição apenas de campanhas em status draft ou scheduled.
 * 
 * Etapas:
 * 1. Configuração básica (nome, descrição, tipo de canal)
 * 2. Seleção de caixas de entrada do mesmo tipo
 * 3. Destinatários (contatos existentes, CSV ou manual)
 * 4. Mensagem (sequência de itens: texto, mídia, delays)
 * 5. Configurações anti-spam
 * 6. Agendamento
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { useBlacklist } from '@/hooks/useBlacklist';
import { normalizeBrazilianPhone } from '@/lib/phone-utils';
import { createLogger } from '@/lib/logger';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  MessageSquare,
  Users,
  FileText,
  Settings,
  Settings2,
  Calendar,
  CalendarDays,
  Clock,
  Inbox,
  Check,
  Plus,
  X,
  AlertTriangle,
  Pencil,
  FileSpreadsheet,
  Shield,
  Search,
  Phone,
  Mail,
  UserPlus,
  Megaphone,
  Zap,
  Filter,
} from 'lucide-react';
import { CampaignTemplateSelector } from './CampaignTemplateSelector';
import { MessageGroupManager } from './MessageGroupManager';
import { CampaignActionBuilder } from './CampaignActionBuilder';
import { SegmentBuilder } from './segment/SegmentBuilder';
import { MessageItem, MessageGroup, CampaignMessageItemRecord, TemplateParams, MAX_CAMPAIGN_RECIPIENTS } from './types';
import { CampaignActionItem } from './campaign-actions-types';
import { 
  CampaignSegment, 
  SegmentPreviewContact, 
  createEmptySegment 
} from '@/types/campaign-segments';
import type { LucideIcon } from 'lucide-react';
// Badge compartilhado de visibilidade (mesmo padrão da tela de Listas)
import VisibilityBadge from '@/components/shared/VisibilityBadge';
import type { EntityVisibility } from '@/hooks/useEntityVisibility';

// ============================================================================
// CONFIGURAÇÃO DOS STEPS
// ============================================================================

interface StepConfig {
  id: number;
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  description: string;
}

const STEPS: StepConfig[] = [
  { 
    id: 1, 
    label: 'Configuração', 
    shortLabel: 'Config',
    icon: Settings,
    description: 'Nome, descrição e tipo de canal da campanha'
  },
  { 
    id: 2, 
    label: 'Caixas de Entrada', 
    shortLabel: 'Inboxes',
    icon: Inbox,
    description: 'Selecione as caixas de entrada para envio'
  },
  { 
    id: 3, 
    label: 'Destinatários', 
    shortLabel: 'Destinos',
    icon: Users,
    description: 'Escolha os contatos que receberão a campanha'
  },
  { 
    id: 4, 
    label: 'Mensagem', 
    shortLabel: 'Mensagem',
    icon: MessageSquare,
    description: 'Configure o conteúdo da mensagem'
  },
  { 
    id: 5, 
    label: 'Delay', 
    shortLabel: 'Delay',
    icon: Clock,
    description: 'Configurações de delay e personalização'
  },
  { 
    id: 6, 
    label: 'Ações', 
    shortLabel: 'Ações',
    icon: Zap,
    description: 'Ações automáticas após cada disparo'
  },
  { 
    id: 7, 
    label: 'Agendamento', 
    shortLabel: 'Agendar',
    icon: CalendarDays,
    description: 'Escolha quando enviar a campanha'
  },
];

// Interface para template do WhatsApp (compatível com CampaignTemplateSelector)
interface WhatsAppTemplate {
  id: string;
  inbox_id: string;
  name: string;
  language: string;
  status: string;
  category: string;
  components: Array<{
    type: string;
    format?: string;
    text?: string;
    example?: {
      body_text?: string[][];
      header_text?: string[];
    };
    buttons?: Array<{
      type: string;
      text?: string;
      url?: string;
      phone_number?: string;
    }>;
  }>;
}

// Interface da campanha para edição
interface Campaign {
  id: string;
  name: string;
  description: string | null;
  channel_type: string;
  message_content: string;
  status: string;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  delay_min_ms?: number;
  delay_max_ms?: number;
  block_size?: number;
  block_delay_min_ms?: number;
  block_delay_max_ms?: number;
  use_contact_name?: boolean;
  verify_whatsapp?: boolean;
  segment_data?: any; // JSONB do banco - dados de segmentação
}

interface EditCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign | null; // Pode ser null durante animação de fechamento
}

// Tipos de canal disponíveis
const channelTypes = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'whatsapp_oficial', label: 'WhatsApp Oficial' },
  { value: 'messenger', label: 'Messenger' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'telegram', label: 'Telegram' },
];

// Interface para inbox
interface Inbox {
  id: string;
  name: string;
  identifier: string;
  channel_type: string;
  status: string;
}

// Interface para contato (paritária com CreateCampaignDialog)
interface Contact {
  id: string;
  name: string | null;
  phone: string | null;
  email?: string | null;
}

// Interface para recipient manual
interface ManualRecipient {
  phone: string;
  name: string;
}

// Interface para lista de campanha
interface CampaignList {
  id: string;
  name: string;
  total_rows: number;
  variable_columns: string[];
  status: string;
  created_at: string;
  // Visibilidade configurada da lista (retornada pela RPC get_campaign_lists)
  visibility: EntityVisibility | null;
}

// Interface para item da lista
interface ListItem {
  id: string;
  phone: string;
  name: string | null;
  email: string | null;
  variables: Record<string, string> | null;
}

// Logger para rastreamento
const editCampaignLogger = createLogger('EditCampaign');

export function EditCampaignDialog({ open, onOpenChange, campaign }: EditCampaignDialogProps) {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();
  
  // Hook para verificar blacklist LGPD
  const { checkBlacklist } = useBlacklist();
  
  // Estado do wizard
  const [step, setStep] = useState(1);
  const totalSteps = 7;
  
  // Estado para ações pós-disparo
  const [campaignActions, setCampaignActions] = useState<CampaignActionItem[]>([]);
  
  // Flag para indicar se já carregou os dados
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Etapa 1: Configuração básica
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [channelType, setChannelType] = useState<'whatsapp' | 'whatsapp_oficial' | 'messenger' | 'instagram' | 'telegram' | 'webchat'>('whatsapp');
  
  // Estado para template (WhatsApp Oficial)
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [templateParams, setTemplateParams] = useState<TemplateParams>({ body_params: [], header_params: [], button_params: [] });
  
  // Etapa 2: Seleção de inboxes
  const [selectedInboxes, setSelectedInboxes] = useState<string[]>([]);
  
  // Etapa 3: Destinatários
  const [recipientSource, setRecipientSource] = useState<'contacts' | 'list' | 'manual' | 'segment'>('contacts');
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [manualRecipients, setManualRecipients] = useState<ManualRecipient[]>([]);
  const [newRecipientPhone, setNewRecipientPhone] = useState('');
  const [newRecipientName, setNewRecipientName] = useState('');
  const [selectedList, setSelectedList] = useState<string | null>(null);
  const [contactSearch, setContactSearch] = useState('');
  // Busca server-side: versão "debounced" para evitar disparar a RPC a cada tecla
  // (paridade com CreateCampaignDialog)
  const [debouncedContactSearch, setDebouncedContactSearch] = useState('');
  // Cache acumulado dos contatos já selecionados — necessário porque a lista
  // exibida vem paginada/buscada do servidor e os selecionados podem sair do
  // array atual (paridade com CreateCampaignDialog)
  const [selectedContactsMap, setSelectedContactsMap] = useState<Record<string, Contact>>({});
  
  // Estados para segmentação avançada
  const [campaignSegment, setCampaignSegment] = useState<CampaignSegment>(createEmptySegment());
  const [segmentContacts, setSegmentContacts] = useState<SegmentPreviewContact[]>([]);
  
  // Verificar se CRM está habilitado para exibir filtros de CRM na segmentação
  const crmEnabled = currentTenant?.crm_enabled ?? false;
  
  // Etapa 4: Grupos de Mensagens (substituiu messageItems)
  const [messageGroups, setMessageGroups] = useState<MessageGroup[]>([
    { id: crypto.randomUUID(), name: 'Grupo 1', items: [], weight: 100 }
  ]);
  
  // Etapa 5: Configurações anti-spam
  const [delayRange, setDelayRange] = useState([1000, 3000]);
  const [blockSize, setBlockSize] = useState(5);
  const [blockDelayRange, setBlockDelayRange] = useState([100, 400]);
  const [useContactName, setUseContactName] = useState(true);
  const [verifyWhatsapp, setVerifyWhatsapp] = useState(true);
  
  // Grupo de Blocos - nova funcionalidade para intervalos variáveis
  const [enableGroupBlock, setEnableGroupBlock] = useState(false);
  const [groupBlockIntervalRange, setGroupBlockIntervalRange] = useState([10, 20]); // Em minutos
  const [groupBlockIntervalUnit, setGroupBlockIntervalUnit] = useState<'minutes' | 'hours'>('minutes');
  const [groupBlockSize, setGroupBlockSize] = useState(20);
  
  // Etapa 6: Agendamento
  const [scheduleType, setScheduleType] = useState<'now' | 'scheduled'>('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  // ============= QUERIES PARA DADOS RELACIONADOS =============

  // Buscar inboxes vinculadas à campanha
  const { data: campaignInboxes } = useQuery({
    queryKey: ['campaign-inboxes', campaign?.id],
    queryFn: async () => {
      if (!campaign?.id) return [];
      const { data, error } = await supabase
        .from('campaign_inboxes')
        .select('inbox_id')
        .eq('campaign_id', campaign.id);
      if (error) throw error;
      return data.map(ci => ci.inbox_id);
    },
    enabled: open && !!campaign?.id,
  });

  // Buscar destinatários da campanha (incluindo list_id para detectar fonte)
  const { data: campaignRecipients } = useQuery({
    queryKey: ['campaign-recipients-edit', campaign?.id],
    queryFn: async () => {
      if (!campaign?.id) return [];
      const { data, error } = await supabase
        .from('campaign_recipients')
        .select('contact_id, phone, contact_name, list_id')
        .eq('campaign_id', campaign.id)
        .order('sort_order');
      if (error) throw error;
      return data;
    },
    enabled: open && !!campaign?.id,
  });

  // Buscar itens de mensagem da campanha
  const { data: campaignMessageItems } = useQuery({
    queryKey: ['campaign-message-items-edit', campaign?.id],
    queryFn: async () => {
      if (!campaign?.id) return [];
      const { data, error } = await supabase
        .from('campaign_message_items')
        .select('*')
        .eq('campaign_id', campaign.id)
        .order('sort_order');
      if (error) throw error;
      return data;
    },
    enabled: open && !!campaign?.id,
  });

  // Buscar grupos de mensagem da campanha
  const { data: campaignMessageGroups } = useQuery({
    queryKey: ['campaign-message-groups-edit', campaign?.id],
    queryFn: async () => {
      if (!campaign?.id) return [];
      const { data, error } = await supabase
        .from('campaign_message_groups')
        .select('*')
        .eq('campaign_id', campaign.id)
        .order('sort_order');
      if (error) throw error;
      return data;
    },
    enabled: open && !!campaign?.id,
  });

  // Buscar ações pós-disparo da campanha
  const { data: campaignActionsData } = useQuery({
    queryKey: ['campaign-actions-edit', campaign?.id],
    queryFn: async () => {
      if (!campaign?.id) return [];
      const { data, error } = await supabase
        .from('campaign_actions')
        .select('*')
        .eq('campaign_id', campaign.id)
        .order('sort_order');
      if (error) throw error;
      return data;
    },
    enabled: open && !!campaign?.id,
  });

  // Buscar inboxes do tenant filtradas por tipo de canal E permissão do usuário.
  // Reaproveita a RPC `get_user_accessible_inboxes` (mesma padrão do
  // StartChatInboxPicker/ChatArea) para respeitar inbox_agents/access_restricted.
  const { data: inboxes } = useQuery({
    queryKey: ['inboxes', currentTenant?.id, user?.id, channelType],
    queryFn: async () => {
      if (!currentTenant?.id || !user?.id) return [];
      const actualChannelType = channelType === 'whatsapp_oficial' ? 'whatsapp_oficial' : channelType;
      const { data, error } = await supabase.rpc('get_user_accessible_inboxes', {
        _user_id: user.id,
        _tenant_id: currentTenant.id,
      });
      if (error) throw error;
      const filtered = (data || []).filter(
        (i: any) => i.channel_type === actualChannelType && i.status === 'active'
      );
      return filtered as Inbox[];
    },
    enabled: !!currentTenant?.id && !!user?.id && open,
  });

  // Buscar templates aprovados das inboxes selecionadas (WhatsApp Oficial)
  const { data: templates } = useQuery({
    queryKey: ['campaign-templates-edit', selectedInboxes],
    queryFn: async () => {
      if (selectedInboxes.length === 0) return [];
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('id, inbox_id, name, language, status, category, components')
        .in('inbox_id', selectedInboxes)
        .eq('status', 'APPROVED');
      if (error) throw error;
      // Cast necessário devido à estrutura JSONB do Supabase
      return (data || []) as unknown as WhatsAppTemplate[];
    },
    enabled: channelType === 'whatsapp_oficial' && selectedInboxes.length > 0,
  });

  // Debounce do termo de busca (300ms) — evita disparar a RPC a cada tecla
  // (paridade com CreateCampaignDialog)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedContactSearch(contactSearch.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearch]);

  // Busca contatos do tenant via RPC server-side (search_campaign_contacts).
  // Substitui o SELECT direto que sofria com o limite de 1.000 linhas do
  // PostgREST e impedia o Edit de exibir contatos já selecionados quando o
  // tenant possui muitos contatos.
  const {
    data: contactsResult,
    isLoading: loadingContacts,
    isFetching: fetchingContacts,
  } = useQuery({
    queryKey: ['campaign-contacts', currentTenant?.id, debouncedContactSearch],
    queryFn: async () => {
      // Cast necessário até types.ts ser regenerado
      const { data, error } = await (supabase.rpc as any)('search_campaign_contacts', {
        p_search: debouncedContactSearch || null,
        p_limit: 10,
        p_offset: 0,
        p_tenant_hint: currentTenant?.id,
      });
      if (error) throw error;
      const rows = (data || []) as Array<Contact & { total_count: number }>;
      const total = rows[0]?.total_count ? Number(rows[0].total_count) : 0;
      return {
        contacts: rows.map(({ total_count: _t, ...c }) => c) as Contact[],
        totalCount: total,
      };
    },
    enabled: open && !!currentTenant?.id && recipientSource === 'contacts',
    placeholderData: (prev) => prev,
  });

  const contacts: Contact[] = contactsResult?.contacts ?? [];
  const contactsTotalCount: number = contactsResult?.totalCount ?? 0;

  // Hidratação dos contatos pré-selecionados (vindos de campaign_recipients).
  // Garante que destinatários salvos apareçam marcados e mantenham nome/telefone
  // mesmo quando não estão na página atual da busca paginada.
  useQuery({
    queryKey: ['campaign-contacts-preselected', campaign?.id, selectedContacts.join(',')],
    queryFn: async () => {
      if (!currentTenant?.id || selectedContacts.length === 0) return [];
      // Identifica IDs que ainda não estão no map para evitar chamadas redundantes
      const missingIds = selectedContacts.filter((id) => !selectedContactsMap[id]);
      if (missingIds.length === 0) return [];
      const { data, error } = await (supabase.rpc as any)('get_campaign_contacts_by_ids', {
        p_contact_ids: missingIds,
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      const rows = (data || []) as Contact[];
      if (rows.length > 0) {
        setSelectedContactsMap((prev) => {
          const next = { ...prev };
          rows.forEach((c) => { next[c.id] = c; });
          return next;
        });
      }
      return rows;
    },
    enabled: open && !!currentTenant?.id && recipientSource === 'contacts' && selectedContacts.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Buscar listas de campanha disponíveis (status = 'ready')
  // IMPORTANTE: usa o RPC get_campaign_lists (SECURITY DEFINER) em vez de
  // consulta direta na tabela — o RPC aplica a regra de visibilidade
  // (private | shared | by_profile | public) via user_can_see_entity,
  // mantendo o modal alinhado à tela "Importação de Listas > Contatos".
  const { data: campaignLists } = useQuery({
    queryKey: ['campaign-lists-visible', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase
        .rpc('get_campaign_lists', { p_tenant_hint: currentTenant.id });
      if (error) throw error;
      return ((data || []) as any[])
        .filter((l) => l.status === 'ready')
        .map((l) => ({
          ...l,
          variable_columns: Array.isArray(l.variable_columns) ? l.variable_columns : [],
        }))
        .sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        ) as CampaignList[];
    },
    enabled: !!currentTenant?.id && open,
  });

  // Buscar itens da lista selecionada (preview)
  const { data: listItems } = useQuery({
    queryKey: ['list-items-preview', selectedList],
    queryFn: async () => {
      if (!selectedList) return [];
      const { data, error } = await supabase
        .from('campaign_list_items')
        .select('id, phone, name, email, variables')
        .eq('list_id', selectedList)
        .order('sort_order')
        .limit(5);
      if (error) throw error;
      return (data || []) as ListItem[];
    },
    enabled: !!selectedList,
  });

  // ============= EFEITO PARA CARREGAR DADOS DA CAMPANHA =============

  useEffect(() => {
    if (open && campaign && !dataLoaded) {
      // Carregar dados básicos da campanha
      setName(campaign.name);
      setDescription(campaign.description || '');
      setChannelType(campaign.channel_type as typeof channelType);
      setDelayRange([campaign.delay_min_ms || 1000, campaign.delay_max_ms || 3000]);
      setBlockSize(campaign.block_size || 5);
      setBlockDelayRange([campaign.block_delay_min_ms || 100, campaign.block_delay_max_ms || 400]);
      setUseContactName(campaign.use_contact_name ?? true);
      setVerifyWhatsapp(campaign.verify_whatsapp ?? true);
      
      // Carregar configurações de Grupo de Blocos (casting necessário pois tipos podem não estar atualizados)
      const campaignData = campaign as any;
      setEnableGroupBlock(campaignData.group_block_enabled ?? false);
      const minMinutes = campaignData.group_block_interval_min_minutes ?? 10;
      const maxMinutes = campaignData.group_block_interval_max_minutes ?? 20;
      // Se valores são >= 60, converter para horas
      if (minMinutes >= 60) {
        setGroupBlockIntervalUnit('hours');
        setGroupBlockIntervalRange([Math.round(minMinutes / 60), Math.round(maxMinutes / 60)]);
      } else {
        setGroupBlockIntervalUnit('minutes');
        setGroupBlockIntervalRange([minMinutes, maxMinutes]);
      }
      setGroupBlockSize(campaignData.group_block_size ?? 20);
      
      // Carregar agendamento
      if (campaign.scheduled_at) {
        setScheduleType('scheduled');
        const date = new Date(campaign.scheduled_at);
        setScheduledDate(date.toISOString().split('T')[0]);
        setScheduledTime(date.toTimeString().slice(0, 5));
      } else {
        setScheduleType('now');
      }
      
      setDataLoaded(true);
    }
  }, [open, campaign, dataLoaded]);

  // Carregar inboxes selecionadas
  useEffect(() => {
    if (campaignInboxes && dataLoaded) {
      setSelectedInboxes(campaignInboxes);
    }
  }, [campaignInboxes, dataLoaded]);

  // Carregar destinatários (incluindo verificação de segmentação e lista)
  useEffect(() => {
    if (campaignRecipients && dataLoaded && campaign) {
      // PRIORIDADE 1: Verificar se a campanha usou segmentação
      const campaignData = campaign as any;
      if (campaignData.segment_data && campaignData.segment_data.groups && campaignData.segment_data.groups.length > 0) {
        setRecipientSource('segment');
        setCampaignSegment(campaignData.segment_data);
        return;
      }
      
      // PRIORIDADE 2: Verificar se os destinatários vieram de uma lista
      const recipientWithList = campaignRecipients.find(r => (r as any).list_id);
      if (recipientWithList && (recipientWithList as any).list_id) {
        setRecipientSource('list');
        setSelectedList((recipientWithList as any).list_id);
        return;
      }

      // PRIORIDADE 3: Contatos existentes ou manual
      const contactIds = campaignRecipients
        .filter(r => r.contact_id)
        .map(r => r.contact_id as string);
      
      const manualOnes = campaignRecipients
        .filter(r => !r.contact_id)
        .map(r => ({ phone: r.phone, name: r.contact_name || '' }));
      
      if (contactIds.length > 0) {
        setRecipientSource('contacts');
        setSelectedContacts(contactIds);
      } else if (manualOnes.length > 0) {
        setRecipientSource('manual');
        setManualRecipients(manualOnes);
      }
    }
  }, [campaignRecipients, dataLoaded, campaign]);

  // Carregar grupos de mensagens e itens
  useEffect(() => {
    if (campaignMessageItems && dataLoaded) {
      // Verificar se é um template (WhatsApp Oficial)
      const templateItem = campaignMessageItems.find(item => item.item_type === 'template');
      if (templateItem && channelType === 'whatsapp_oficial') {
        if (templates && templates.length > 0) {
          const foundTemplate = templates.find(t => t.name === templateItem.template_name);
          if (foundTemplate) {
            setSelectedTemplate(foundTemplate);
          }
        }
        const params = templateItem.template_params as TemplateParams | null;
        if (params) {
          setTemplateParams({
            body_params: params.body_params || [],
            header_params: params.header_params || [],
            button_params: params.button_params || [],
          });
        }
      }

      // Montar estrutura de grupos de mensagens
      if (campaignMessageGroups && campaignMessageGroups.length > 0) {
        // Campanha tem grupos - agrupar itens por group_id
        const groups: MessageGroup[] = campaignMessageGroups.map(group => ({
          id: group.id,
          name: group.name,
          weight: group.weight,
          items: campaignMessageItems
            .filter(item => item.group_id === group.id)
            .map((item, index) => ({
              id: item.id,
              item_type: item.item_type as MessageItem['item_type'],
              sort_order: index,
              text_content: item.text_content || undefined,
              media_url: item.media_url || undefined,
              media_filename: item.media_filename || undefined,
              media_mime_type: item.media_mime_type || undefined,
              delay_seconds: item.delay_seconds || undefined,
              contact_name: item.contact_name || undefined,
              contact_phone: item.contact_phone || undefined,
              contact_business_description: item.contact_business_description || undefined,
              template_name: item.template_name || undefined,
              template_language: item.template_language || undefined,
              template_params: (item.template_params as TemplateParams | null) || undefined,
              buttons_config: item.buttons_config as unknown as MessageItem['buttons_config'],
            }))
        }));
        setMessageGroups(groups);
      } else {
        // Fallback: campanha legada sem grupos - criar grupo único com todos os itens
        const legacyItems: MessageItem[] = campaignMessageItems.map((item, index) => ({
          id: item.id,
          item_type: item.item_type as MessageItem['item_type'],
          sort_order: index,
          text_content: item.text_content || undefined,
          media_url: item.media_url || undefined,
          media_filename: item.media_filename || undefined,
          media_mime_type: item.media_mime_type || undefined,
          delay_seconds: item.delay_seconds || undefined,
          contact_name: item.contact_name || undefined,
          contact_phone: item.contact_phone || undefined,
          contact_business_description: item.contact_business_description || undefined,
          template_name: item.template_name || undefined,
          template_language: item.template_language || undefined,
          template_params: (item.template_params as TemplateParams | null) || undefined,
          buttons_config: item.buttons_config as unknown as MessageItem['buttons_config'],
        }));
        setMessageGroups([{
          id: crypto.randomUUID(),
          name: 'Grupo 1',
          items: legacyItems,
          weight: 100
        }]);
      }
    }
  }, [campaignMessageItems, campaignMessageGroups, dataLoaded, templates, channelType]);

  // Carregar ações pós-disparo
  useEffect(() => {
    if (campaignActionsData && dataLoaded) {
      const actions: CampaignActionItem[] = campaignActionsData.map(action => ({
        id: action.id,
        action_type: action.action_type as CampaignActionItem['action_type'],
        action_value: action.action_value as Record<string, string>,
        execution_condition: action.execution_condition as CampaignActionItem['execution_condition'],
        sort_order: action.sort_order,
      }));
      setCampaignActions(actions);
    }
  }, [campaignActionsData, dataLoaded]);

  // Reset ao fechar
  useEffect(() => {
    if (!open) {
      setStep(1);
      setDataLoaded(false);
      setName('');
      setDescription('');
      setChannelType('whatsapp');
      setSelectedInboxes([]);
      setSelectedContacts([]);
      setManualRecipients([]);
      setMessageGroups([{ id: crypto.randomUUID(), name: 'Grupo 1', items: [], weight: 100 }]);
      setScheduleType('now');
      setScheduledDate('');
      setScheduledTime('');
      setSelectedList(null);
      // Reset estados de template
      setSelectedTemplate(null);
      setTemplateParams({ body_params: [], header_params: [], button_params: [] });
      // Reset estados de Grupo de Blocos
      setEnableGroupBlock(false);
      setGroupBlockIntervalRange([10, 20]);
      setGroupBlockIntervalUnit('minutes');
      setGroupBlockSize(20);
      // Reset ações pós-disparo
      setCampaignActions([]);
      // Reset estados de segmentação
      setCampaignSegment(createEmptySegment());
      setSegmentContacts([]);
    }
  }, [open]);

  // ============= MUTATION PARA ATUALIZAR CAMPANHA =============

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');
      if (!campaign) throw new Error('Campanha não encontrada');

      // Calcula total de destinatários
      let totalRecipients = 0;
      if (recipientSource === 'contacts') {
        totalRecipients = selectedContacts.length;
      } else if (recipientSource === 'list') {
        const selectedListData = campaignLists?.find(l => l.id === selectedList);
        totalRecipients = selectedListData?.total_rows || 0;
      } else if (recipientSource === 'segment') {
        totalRecipients = segmentContacts.length;
      } else {
        totalRecipients = manualRecipients.length;
      }

      let scheduledAt = null;
      if (scheduleType === 'scheduled' && scheduledDate && scheduledTime) {
        scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      }

      // Encontrar primeiro item de texto em qualquer grupo para message_content
      const allItems = messageGroups.flatMap(g => g.items);
      const firstTextItem = allItems.find(item => item.item_type === 'text');
      const messageContent = firstTextItem?.text_content || '';

      // 1. Atualizar dados da campanha
      const { error: campaignError } = await supabase
        .from('campaigns')
        .update({
          name,
          description: description || null,
          channel_type: channelType,
          message_content: messageContent,
          delay_min_ms: delayRange[0],
          delay_max_ms: delayRange[1],
          block_size: blockSize,
          block_delay_min_ms: blockDelayRange[0],
          block_delay_max_ms: blockDelayRange[1],
          use_contact_name: useContactName,
          verify_whatsapp: verifyWhatsapp,
          // Grupo de Blocos - intervalo variável entre grupos
          group_block_enabled: enableGroupBlock,
          group_block_interval_min_minutes: groupBlockIntervalUnit === 'hours' 
            ? groupBlockIntervalRange[0] * 60 
            : groupBlockIntervalRange[0],
          group_block_interval_max_minutes: groupBlockIntervalUnit === 'hours' 
            ? groupBlockIntervalRange[1] * 60 
            : groupBlockIntervalRange[1],
          group_block_size: groupBlockSize,
          status: scheduleType === 'scheduled' ? 'scheduled' : campaign.status as 'draft' | 'scheduled',
          scheduled_at: scheduledAt,
          total_recipients: totalRecipients,
          // Segmentação: salvar critérios se fonte for segmento, senão limpar (cast para Json)
          segment_data: recipientSource === 'segment' ? JSON.parse(JSON.stringify(campaignSegment)) : null,
        })
        .eq('id', campaign.id);

      if (campaignError) throw campaignError;

      // 2. Sincronizar inboxes (delete + insert)
      await supabase
        .from('campaign_inboxes')
        .delete()
        .eq('campaign_id', campaign.id);

      if (selectedInboxes.length > 0) {
        const inboxRecords = selectedInboxes.map(inboxId => ({
          campaign_id: campaign.id,
          inbox_id: inboxId,
        }));
        const { error: inboxError } = await supabase
          .from('campaign_inboxes')
          .insert(inboxRecords);
        if (inboxError) throw inboxError;
      }

      // 3. Sincronizar destinatários (delete + insert)
      await supabase
        .from('campaign_recipients')
        .delete()
        .eq('campaign_id', campaign.id);

      // 3. Preparar destinatários baseado na fonte selecionada
      let recipientRecords: Array<{
        campaign_id: string;
        contact_id?: string;
        phone: string;
        contact_name?: string | null;
        sort_order: number;
        list_id?: string;
        variables?: Record<string, string>;
      }> = [];

      if (recipientSource === 'contacts') {
        // Fonte: Contatos existentes
        // IMPORTANTE: usar selectedContactsMap (cache acumulado) como fonte
        // primária — o array `contacts` está paginado pela RPC e pode não conter
        // todos os IDs selecionados. Fallback no array `contacts` por segurança.
        recipientRecords = selectedContacts.map((contactId, index) => {
          const contact = selectedContactsMap[contactId] ?? contacts?.find((c) => c.id === contactId);
          return {
            campaign_id: campaign.id,
            contact_id: contactId,
            phone: normalizeBrazilianPhone(contact?.phone || ''),
            contact_name: contact?.name || null,
            sort_order: index,
          };
        });
      } else if (recipientSource === 'list') {
        // Fonte: Lista de campanha (buscar todos os itens da lista)
        const { data: allListItems, error: listItemsError } = await supabase
          .from('campaign_list_items')
          .select('id, phone, name, email, variables')
          .eq('list_id', selectedList)
          .order('sort_order');

        if (listItemsError) throw listItemsError;

        recipientRecords = (allListItems || []).map((item, index) => ({
          campaign_id: campaign.id,
          phone: normalizeBrazilianPhone(item.phone),
          contact_name: item.name || null,
          sort_order: index,
          list_id: selectedList!,
          variables: (item.variables as Record<string, string>) || {},
        }));
      } else {
        // Fonte: Manual
        recipientRecords = manualRecipients.map((recipient, index) => ({
          campaign_id: campaign.id,
          phone: normalizeBrazilianPhone(recipient.phone),
          contact_name: recipient.name || null,
          sort_order: index,
        }));
      }

      // =========================================================================
      // PROTEÇÃO LGPD: Filtrar recipients que estão na blacklist
      // =========================================================================
      let filteredRecipients = recipientRecords;
      let blacklistedCount = 0;
      
      if (recipientRecords.length > 0) {
        const phones = recipientRecords.map(r => r.phone).filter(p => p);
        const blacklistResult = await checkBlacklist(phones);
        
        // Filtrar recipients que não estão na blacklist
        filteredRecipients = recipientRecords.filter(r => !r.phone || !blacklistResult.has(r.phone));
        blacklistedCount = recipientRecords.length - filteredRecipients.length;
        
        if (blacklistedCount > 0) {
          editCampaignLogger.info(`[EditCampaign] ${blacklistedCount} contatos da blacklist LGPD foram excluídos`);
          sonnerToast.info(`${blacklistedCount} contato(s) da blacklist LGPD foram excluídos da campanha`);
        }
      }

      // Reindexar sort_order após filtragem
      filteredRecipients = filteredRecipients.map((r, idx) => ({ ...r, sort_order: idx }));

      if (filteredRecipients.length > 0) {
        const { error: recipientError } = await supabase
          .from('campaign_recipients')
          .insert(filteredRecipients);
        if (recipientError) throw recipientError;
      }

      // 4. Sincronizar grupos e itens de mensagem (delete + insert)
      // Deletar grupos existentes (cascade deleta itens via FK)
      await supabase
        .from('campaign_message_groups')
        .delete()
        .eq('campaign_id', campaign.id);

      // Deletar items sem grupo (templates) que não são deletados pelo cascade
      await supabase
        .from('campaign_message_items')
        .delete()
        .eq('campaign_id', campaign.id)
        .is('group_id', null);

      // Para WhatsApp Oficial, criar item de template (sem grupo)
      if (channelType === 'whatsapp_oficial' && selectedTemplate) {
        const templateRecord: CampaignMessageItemRecord = {
          campaign_id: campaign.id,
          item_type: 'template',
          sort_order: 0,
          template_name: selectedTemplate.name,
          template_language: selectedTemplate.language || 'pt_BR',
          template_params: templateParams,
        };

        const { error: templateError } = await supabase
          .from('campaign_message_items')
          .insert([templateRecord]);

        if (templateError) {
          console.error('[EditCampaignDialog] Erro ao salvar template:', templateError);
        }
      } else {
        // Salvar grupos e seus itens
        for (let groupIndex = 0; groupIndex < messageGroups.length; groupIndex++) {
          const group = messageGroups[groupIndex];
          
          // Inserir grupo
          const { data: savedGroup, error: groupError } = await supabase
            .from('campaign_message_groups')
            .insert({
              campaign_id: campaign.id,
              name: group.name,
              sort_order: groupIndex,
              weight: group.weight,
            })
            .select()
            .single();

          if (groupError) {
            console.error('[EditCampaignDialog] Erro ao salvar grupo:', groupError);
            continue;
          }

          // Inserir itens do grupo
          if (group.items.length > 0) {
            const itemRecords: CampaignMessageItemRecord[] = group.items.map((item, idx) => ({
              campaign_id: campaign.id,
              group_id: savedGroup.id,
              item_type: item.item_type,
              sort_order: idx,
              text_content: item.text_content || null,
              media_url: item.media_url || null,
              media_filename: item.media_filename || null,
              media_mime_type: item.media_mime_type || null,
              delay_seconds: item.delay_seconds || 0,
              buttons_config: item.buttons_config || null,
              contact_name: item.contact_name || null,
              contact_phone: item.contact_phone || null,
              contact_business_description: item.contact_business_description || null,
              template_name: item.template_name || null,
              template_language: item.template_language || null,
              template_params: item.template_params || null,
            }));

            const { error: itemsError } = await supabase
              .from('campaign_message_items')
              .insert(itemRecords);

            if (itemsError) {
              console.error('[EditCampaignDialog] Erro ao salvar itens do grupo:', itemsError);
            }
          }
        }
      }

      // 5. Sincronizar ações pós-disparo (delete + insert)
      await supabase
        .from('campaign_actions')
        .delete()
        .eq('campaign_id', campaign.id);

      if (campaignActions.length > 0) {
        const actionRecords = campaignActions.map((action, idx) => ({
          campaign_id: campaign.id,
          action_type: action.action_type,
          action_value: action.action_value,
          execution_condition: action.execution_condition,
          sort_order: idx,
        }));

        const { error: actionsError } = await supabase
          .from('campaign_actions')
          .insert(actionRecords);

        if (actionsError) {
          console.error('[EditCampaignDialog] Erro ao salvar ações:', actionsError);
        }
      }

      return campaign;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign?.id] });
      toast({ title: 'Campanha atualizada com sucesso!' });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({ 
        title: 'Erro ao atualizar campanha', 
        description: error.message,
        variant: 'destructive' 
      });
    },
  });

  // ============= CÁLCULO DE RECIPIENTES PARA RESUMO =============

  // Calcula contagem de destinatários - usa total_rows da lista para validação imediata
  const recipientCount = useMemo(() => {
    if (recipientSource === 'contacts') return selectedContacts.length;
    if (recipientSource === 'list') {
      // Usa total_rows da lista selecionada (disponível imediatamente)
      const selectedListData = campaignLists?.find(l => l.id === selectedList);
      return selectedListData?.total_rows || listItems?.length || 0;
    }
    if (recipientSource === 'manual') return manualRecipients.length;
    if (recipientSource === 'segment') return segmentContacts.length;
    return 0;
  }, [recipientSource, selectedContacts.length, selectedList, campaignLists, listItems, manualRecipients.length, segmentContacts.length]);

  // ============= NAVEGAÇÃO =============

  // Navega para step anterior
  const handleBack = () => {
    if (step > 1) {
      setStep(s => s - 1);
    }
  };

  // Navega para próximo step
  const handleNext = () => {
    if (step < totalSteps && isStepValid()) {
      setStep(s => s + 1);
    }
  };

  // ============= VALIDAÇÃO E HELPERS =============

  const isStepValid = () => {
    switch (step) {
      case 1:
        return name.trim().length > 0;
      case 2:
        return selectedInboxes.length > 0;
      case 3: {
        // Trava de limite máximo de destinatários por campanha
        if (recipientCount > MAX_CAMPAIGN_RECIPIENTS) return false;
        if (recipientSource === 'contacts') return selectedContacts.length > 0;
        if (recipientSource === 'list') return selectedList !== null;
        if (recipientSource === 'segment') return segmentContacts.length > 0;
        return manualRecipients.length > 0;
      }
      case 4:
        // Para WhatsApp Oficial, validar se template foi selecionado e variáveis preenchidas
        if (channelType === 'whatsapp_oficial') {
          if (!selectedTemplate) return false;
          // Validar que todas as variáveis do body foram preenchidas
          const bodyCompEdit = selectedTemplate.components?.find((c: any) => c.type === 'BODY');
          if (bodyCompEdit?.text) {
            const varMatchesEdit = bodyCompEdit.text.match(/\{\{\d+\}\}/g);
            const varCountEdit = varMatchesEdit ? varMatchesEdit.length : 0;
            if (varCountEdit > 0) {
              const bodyParamsEdit = templateParams.body_params || [];
              const allFilledEdit = Array.from({ length: varCountEdit }).every((_, i) =>
                bodyParamsEdit[i] && bodyParamsEdit[i].trim().length > 0
              );
              if (!allFilledEdit) return false;
            }
          }
          return true;
        }
        // Validar se pelo menos um grupo tem itens
        return messageGroups.some(g => g.items.length > 0);
      case 5:
        return true;
      case 6:
        if (scheduleType === 'scheduled') {
          return scheduledDate && scheduledTime;
        }
        return true;
      default:
        return true;
    }
  };

  const addManualRecipient = () => {
    if (!newRecipientPhone.trim()) return;
    if (manualRecipients.some(r => r.phone === newRecipientPhone.trim())) {
      toast({ title: 'Telefone já adicionado', variant: 'destructive' });
      return;
    }
    setManualRecipients(prev => [...prev, { 
      phone: newRecipientPhone.trim(), 
      name: newRecipientName.trim() 
    }]);
    setNewRecipientPhone('');
    setNewRecipientName('');
  };

  const removeManualRecipient = (phone: string) => {
    setManualRecipients(prev => prev.filter(r => r.phone !== phone));
  };

  const toggleInbox = (inboxId: string) => {
    setSelectedInboxes(prev => 
      prev.includes(inboxId) 
        ? prev.filter(id => id !== inboxId)
        : [...prev, inboxId]
    );
  };

  // Alterna seleção de um contato. Recebe o objeto Contact (não só o id) para
  // atualizar também o selectedContactsMap — necessário porque a lista exibida
  // vem paginada do servidor (paridade com CreateCampaignDialog).
  const toggleContact = (contact: Contact) => {
    setSelectedContacts((prev) =>
      prev.includes(contact.id)
        ? prev.filter((id) => id !== contact.id)
        : [...prev, contact.id],
    );
    setSelectedContactsMap((prev) => {
      if (prev[contact.id]) {
        const next = { ...prev };
        delete next[contact.id];
        return next;
      }
      return { ...prev, [contact.id]: contact };
    });
  };

  // Seleciona/desmarca todos os contatos VISÍVEIS na página atual (a busca é
  // server-side e paginada, então atua só sobre `contacts`).
  const selectAllContacts = () => {
    if (!contacts || contacts.length === 0) return;
    const currentIds = contacts.map((c) => c.id);
    const allVisibleSelected = currentIds.every((id) => selectedContacts.includes(id));
    if (allVisibleSelected) {
      setSelectedContacts((prev) => prev.filter((id) => !currentIds.includes(id)));
      setSelectedContactsMap((prev) => {
        const next = { ...prev };
        currentIds.forEach((id) => { delete next[id]; });
        return next;
      });
    } else {
      setSelectedContacts((prev) => Array.from(new Set([...prev, ...currentIds])));
      setSelectedContactsMap((prev) => {
        const next = { ...prev };
        contacts.forEach((c) => { next[c.id] = c; });
        return next;
      });
    }
  };

  // ============= RENDERIZAÇÃO DAS ETAPAS =============

  const renderStepContent = () => {
    switch (step) {
      // =========== STEP 1: CONFIGURAÇÃO ===========
      case 1:
        return (
          <div className="space-y-6">
            {/* Nome da campanha */}
            <div className="space-y-2">
              <Label htmlFor="name">Nome da campanha *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Black Friday 2024"
                className="h-11"
              />
              <p className="text-xs text-muted-foreground">
                Nome para identificar a campanha internamente
              </p>
            </div>
            
            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição (opcional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva o objetivo da campanha"
                rows={3}
              />
            </div>
            
            {/* Tipo de canal - Cards visuais */}
            <div className="space-y-2">
              <Label>Tipo de canal *</Label>
              <RadioGroup
                value={channelType === 'whatsapp_oficial' ? 'whatsapp_oficial' : 'whatsapp'}
                onValueChange={(value) => {
                  setChannelType(value as typeof channelType);
                  setSelectedInboxes([]);
                }}
                className="grid gap-3"
              >
                {/* WhatsApp */}
                <Label
                  htmlFor="channel-whatsapp"
                  className={cn(
                    "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                    "hover:border-primary/50 hover:bg-accent/50",
                    channelType === 'whatsapp' 
                      ? "border-primary bg-primary/5" 
                      : "border-border"
                  )}
                >
                  <RadioGroupItem value="whatsapp" id="channel-whatsapp" className="sr-only" />
                  <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium block">WhatsApp</span>
                    <span className="text-xs text-muted-foreground">Conexão via QR Code</span>
                  </div>
                  {channelType === 'whatsapp' && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </Label>

                {/* WhatsApp Oficial */}
                <Label
                  htmlFor="channel-oficial"
                  className={cn(
                    "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                    "hover:border-primary/50 hover:bg-accent/50",
                    channelType === 'whatsapp_oficial' 
                      ? "border-primary bg-primary/5" 
                      : "border-border"
                  )}
                >
                  <RadioGroupItem value="whatsapp_oficial" id="channel-oficial" className="sr-only" />
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium block">WhatsApp Oficial</span>
                    <span className="text-xs text-muted-foreground">API Business (Templates)</span>
                  </div>
                  {channelType === 'whatsapp_oficial' && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </Label>
              </RadioGroup>
            </div>
          </div>
        );

      // =========== STEP 2: INBOXES ===========
      case 2:
        return (
          <div className="space-y-4">
            {inboxes && inboxes.length > 0 ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Selecione uma ou mais caixas de entrada para enviar a campanha.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {inboxes.map((inbox) => (
                    <div
                      key={inbox.id}
                      onClick={() => toggleInbox(inbox.id)}
                      className={cn(
                        "p-4 rounded-lg border-2 cursor-pointer transition-all",
                        "hover:border-primary/50 hover:bg-accent/50",
                        selectedInboxes.includes(inbox.id)
                          ? "border-primary bg-primary/5"
                          : "border-border"
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center",
                            inbox.status === 'active' ? "bg-green-500/10" : "bg-yellow-500/10"
                          )}>
                            <Inbox className={cn(
                              "h-5 w-5",
                              inbox.status === 'active' ? "text-green-500" : "text-yellow-500"
                            )} />
                          </div>
                          <div>
                            <p className="font-medium">{inbox.name}</p>
                            <p className="text-xs text-muted-foreground">{inbox.identifier}</p>
                          </div>
                        </div>
                        {selectedInboxes.includes(inbox.id) && (
                          <Check className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <Inbox className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">
                  Nenhuma caixa de entrada ativa encontrada para este tipo de canal.
                </p>
              </div>
            )}
          </div>
        );

      // =========== STEP 3: DESTINATÁRIOS ===========
      case 3:
        return (
          <div className="space-y-4">
            {/* Alerta de limite máximo de destinatários excedido */}
            {recipientCount > MAX_CAMPAIGN_RECIPIENTS && (
              <div className="flex items-start gap-3 p-4 rounded-lg border border-destructive/50 bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-destructive">
                    Limite máximo: {MAX_CAMPAIGN_RECIPIENTS.toLocaleString('pt-BR')} contatos por campanha
                  </p>
                  <p className="text-xs text-destructive/80 mt-1">
                    Você selecionou {recipientCount.toLocaleString('pt-BR')} contatos. Para realizar disparos maiores, entre em contato com o comercial UaizyChat.
                  </p>
                </div>
              </div>
            )}
            {/* Tabs de fonte de destinatários - 4 abas */}
            {/* Ordem paritária com CreateCampaignDialog: Lista CSV → Contatos → Manual → Segmento */}
            <Tabs value={recipientSource} onValueChange={(v) => setRecipientSource(v as 'contacts' | 'list' | 'manual' | 'segment')}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="list" className="gap-1 text-xs sm:text-sm">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Lista CSV</span>
                </TabsTrigger>
                <TabsTrigger value="contacts" className="gap-1 text-xs sm:text-sm">
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">Contatos</span>
                </TabsTrigger>
                <TabsTrigger value="manual" className="gap-1 text-xs sm:text-sm">
                  <UserPlus className="h-4 w-4" />
                  <span className="hidden sm:inline">Manual</span>
                </TabsTrigger>
                <TabsTrigger value="segment" className="gap-1 text-xs sm:text-sm">
                  <Filter className="h-4 w-4" />
                  <span className="hidden sm:inline">Segmento</span>
                </TabsTrigger>
              </TabsList>

              {/* Tab: Contatos existentes — paridade com CreateCampaignDialog */}
              <TabsContent value="contacts" className="mt-4 space-y-4">
                {/* Busca e ações — sempre visíveis (busca é server-side) */}
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Buscar contatos (nome, telefone ou e-mail)..."
                      className="pl-9"
                    />
                    {fetchingContacts && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllContacts}
                    disabled={contacts.length === 0}
                  >
                    {contacts.length > 0 && contacts.every((c) => selectedContacts.includes(c.id))
                      ? 'Desmarcar visíveis'
                      : 'Selecionar visíveis'}
                  </Button>
                </div>

                {/* Lista de contatos */}
                <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                  {loadingContacts ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : contacts.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
                      <p className="text-muted-foreground">
                        {debouncedContactSearch
                          ? 'Nenhum contato encontrado para a busca'
                          : 'Nenhum contato cadastrado'}
                      </p>
                    </div>
                  ) : (
                    contacts.map((contact) => (
                      <div
                        key={contact.id}
                        onClick={() => toggleContact(contact)}
                        className={cn(
                          'flex items-center gap-3 p-3 cursor-pointer border-b last:border-b-0 transition-colors',
                          'hover:bg-accent/50',
                          selectedContacts.includes(contact.id) && 'bg-primary/5',
                        )}
                      >
                        <Checkbox
                          checked={selectedContacts.includes(contact.id)}
                          className="pointer-events-none"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {contact.name || 'Sem nome'}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {contact.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {contact.phone}
                              </span>
                            )}
                            {contact.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {contact.email}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Resumo — selecionados + total disponível no servidor */}
                <p className="text-sm text-muted-foreground">
                  {selectedContacts.length} contato(s) selecionado(s)
                  {contactsTotalCount > 0 && (
                    <>
                      {' '}— exibindo {contacts.length} de {contactsTotalCount.toLocaleString('pt-BR')}.
                      {contactsTotalCount > contacts.length && ' Use a busca para refinar.'}
                    </>
                  )}
                </p>
              </TabsContent>


              {/* Tab: Lista CSV */}
              <TabsContent value="list" className="mt-4 space-y-4">
                {campaignLists && campaignLists.length > 0 ? (
                  <>
                    <div className="space-y-2">
                      {campaignLists.map((list) => (
                        <div
                          key={list.id}
                          onClick={() => setSelectedList(list.id)}
                          className={cn(
                            "p-4 rounded-lg border-2 cursor-pointer transition-all",
                            "hover:border-primary/50 hover:bg-accent/50",
                            selectedList === list.id
                              ? "border-primary bg-primary/5"
                              : "border-border"
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{list.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {list.total_rows} contatos
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Badge de visibilidade da lista (Todos/Compartilhada/Por perfil/Privada) */}
                              <VisibilityBadge visibility={list.visibility ?? 'public'} />
                              {list.variable_columns.length > 0 && (
                                <Badge variant="secondary" className="text-xs">
                                  {list.variable_columns.length} variáveis
                                </Badge>
                              )}
                              {selectedList === list.id && (
                                <Check className="h-5 w-5 text-primary" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {selectedList && listItems && listItems.length > 0 && (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-sm font-medium mb-2">
                          Preview ({listItems.length} destinatários):
                        </p>
                        <div className="text-xs text-muted-foreground space-y-1">
                          {listItems.slice(0, 3).map((item, idx) => (
                            <p key={idx}>
                              • {item.name || 'Sem nome'} - {item.phone}
                            </p>
                          ))}
                          {listItems.length > 3 && (
                            <p>... e mais {listItems.length - 3} contatos</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-muted-foreground mb-2">Nenhuma lista importada</p>
                    <p className="text-xs text-muted-foreground">
                      Vá para Listas no menu para importar um arquivo CSV
                    </p>
                  </div>
                )}
              </TabsContent>

              {/* Tab: Manual */}
              <TabsContent value="manual" className="mt-4 space-y-4">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      value={newRecipientPhone}
                      onChange={(e) => setNewRecipientPhone(e.target.value)}
                      placeholder="Telefone (ex: 5562999999999)"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      value={newRecipientName}
                      onChange={(e) => setNewRecipientName(e.target.value)}
                      placeholder="Nome (opcional)"
                    />
                  </div>
                  <Button onClick={addManualRecipient} disabled={!newRecipientPhone.trim()}>
                    <UserPlus className="h-4 w-4" />
                  </Button>
                </div>

                {manualRecipients.length > 0 && (
                  <div className="border rounded-lg max-h-[250px] overflow-y-auto">
                    {manualRecipients.map((recipient, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 border-b last:border-b-0"
                      >
                        <div>
                          <p className="font-medium">{recipient.name || 'Sem nome'}</p>
                          <p className="text-xs text-muted-foreground">{recipient.phone}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeManualRecipient(recipient.phone)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-sm text-muted-foreground">
                  {manualRecipients.length} destinatários adicionados
                </p>
              </TabsContent>

              {/* Tab: Segmento - Audiência dinâmica baseada em filtros */}
              <TabsContent value="segment" className="mt-4 space-y-4">
                <SegmentBuilder
                  segment={campaignSegment}
                  onSegmentChange={setCampaignSegment}
                  onContactsChange={setSegmentContacts}
                  crmEnabled={crmEnabled}
                />
              </TabsContent>
            </Tabs>
          </div>
        );

      case 4:
        // Extrair variáveis disponíveis da lista selecionada
        const availableVariables = recipientSource === 'list' && selectedList
          ? ((campaignLists?.find(l => l.id === selectedList)?.variable_columns || []) as string[])
          : [];
        
        // Para WhatsApp Oficial, mostrar seletor de template
        if (channelType === 'whatsapp_oficial') {
          return (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Selecione um template aprovado para enviar na campanha.
                {availableVariables.length > 0 && (
                  <> Você pode usar variáveis da lista para personalizar as mensagens.</>
                )}
              </div>
              <CampaignTemplateSelector
                selectedInboxes={selectedInboxes}
                selectedTemplate={selectedTemplate}
                onSelectTemplate={setSelectedTemplate}
                templateParams={templateParams}
                onTemplateParamsChange={setTemplateParams}
                availableListVariables={availableVariables}
              />
            </div>
          );
        }
        
        return (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Configure os grupos de mensagens que serão enviados. Cada destinatário receberá um grupo aleatoriamente baseado nos pesos configurados.
              {availableVariables.length > 0 
                ? ' Clique nas variáveis disponíveis para inserir no texto.'
                : <> Use <code className="bg-muted px-1 rounded">{'{{nome}}'}</code> para personalizar com o nome do contato.</>
              }
            </div>
            <MessageGroupManager
              groups={messageGroups}
              onGroupsChange={setMessageGroups}
              availableVariables={availableVariables}
            />
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <Label>Delay entre mensagens: {(delayRange[0] / 1000).toFixed(1)}s - {(delayRange[1] / 1000).toFixed(1)}s</Label>
              <Slider
                value={delayRange}
                onValueChange={setDelayRange}
                min={500}
                max={10000}
                step={100}
              />
            </div>
            
            <div className="space-y-3">
              <Label>Tamanho do bloco: {blockSize} mensagens</Label>
              <Slider
                value={[blockSize]}
                onValueChange={(v) => setBlockSize(v[0])}
                min={1}
                max={20}
                step={1}
              />
            </div>
            
            <div className="space-y-3">
              <Label>Delay entre blocos: {(blockDelayRange[0] / 1000).toFixed(1)}s - {(blockDelayRange[1] / 1000).toFixed(1)}s</Label>
              <Slider
                value={blockDelayRange}
                onValueChange={setBlockDelayRange}
                min={50}
                max={2000}
                step={50}
              />
            </div>

            <Separator />

            {/* Grupo de Blocos - Nova funcionalidade */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Grupo de Blocos</Label>
                  <p className="text-xs text-muted-foreground">
                    Agrupa envios com intervalos variáveis para evitar detecção
                  </p>
                </div>
                <Switch checked={enableGroupBlock} onCheckedChange={setEnableGroupBlock} />
              </div>

              {enableGroupBlock && (
                <div className="space-y-4 p-4 rounded-lg bg-muted/50 border">
                  {/* Intervalo entre grupos - Range slider */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>
                        Intervalo entre grupos: {groupBlockIntervalRange[0]} - {groupBlockIntervalRange[1]} {groupBlockIntervalUnit === 'hours' ? 'h' : 'min'}
                      </Label>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant={groupBlockIntervalUnit === 'minutes' ? 'default' : 'outline'}
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => {
                            if (groupBlockIntervalUnit !== 'minutes') {
                              setGroupBlockIntervalRange([
                                groupBlockIntervalRange[0] * 60,
                                groupBlockIntervalRange[1] * 60
                              ]);
                              setGroupBlockIntervalUnit('minutes');
                            }
                          }}
                        >
                          Min
                        </Button>
                        <Button
                          type="button"
                          variant={groupBlockIntervalUnit === 'hours' ? 'default' : 'outline'}
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => {
                            if (groupBlockIntervalUnit !== 'hours') {
                              setGroupBlockIntervalRange([
                                Math.max(1, Math.round(groupBlockIntervalRange[0] / 60)),
                                Math.max(1, Math.round(groupBlockIntervalRange[1] / 60))
                              ]);
                              setGroupBlockIntervalUnit('hours');
                            }
                          }}
                        >
                          Horas
                        </Button>
                      </div>
                    </div>
                    <Slider
                      value={groupBlockIntervalRange}
                      onValueChange={setGroupBlockIntervalRange}
                      min={groupBlockIntervalUnit === 'hours' ? 1 : 5}
                      max={groupBlockIntervalUnit === 'hours' ? 24 : 120}
                      step={groupBlockIntervalUnit === 'hours' ? 1 : 5}
                    />
                    <p className="text-xs text-muted-foreground">
                      Sistema escolherá aleatoriamente um intervalo entre esses valores
                    </p>
                  </div>

                  {/* Mensagens por grupo */}
                  <div className="space-y-3">
                    <Label>Mensagens por grupo: {groupBlockSize}</Label>
                    <Slider
                      value={[groupBlockSize]}
                      onValueChange={(v) => setGroupBlockSize(v[0])}
                      min={5}
                      max={200}
                      step={5}
                    />
                    <p className="text-xs text-muted-foreground">
                      Total de mensagens enviadas antes do intervalo do grupo
                    </p>
                  </div>

                  {/* Resumo visual */}
                  <div className="p-3 rounded-md bg-background border text-xs space-y-1">
                    <p className="font-medium text-muted-foreground">Resumo:</p>
                    <p>
                      A cada <span className="font-semibold text-primary">{groupBlockSize}</span> mensagens, 
                      o sistema aguardará entre{' '}
                      <span className="font-semibold text-primary">
                        {groupBlockIntervalRange[0]}-{groupBlockIntervalRange[1]} {groupBlockIntervalUnit === 'hours' ? 'horas' : 'minutos'}
                      </span>{' '}
                      antes de enviar o próximo grupo.
                    </p>
                    <p className="text-muted-foreground">
                      Blocos de {blockSize} msgs com delay de {(delayRange[0]/1000).toFixed(1)}-{(delayRange[1]/1000).toFixed(1)}s entre msgs
                      {' '}e {(blockDelayRange[0]/1000).toFixed(1)}-{(blockDelayRange[1]/1000).toFixed(1)}s entre blocos
                    </p>
                  </div>
                </div>
              )}
            </div>

            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Usar nome do contato</Label>
                <p className="text-xs text-muted-foreground">Substitui {"{{nome}}"} pelo nome</p>
              </div>
              <Switch checked={useContactName} onCheckedChange={setUseContactName} />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Verificar WhatsApp</Label>
                <p className="text-xs text-muted-foreground">Verifica se número tem WhatsApp</p>
              </div>
              <Switch checked={verifyWhatsapp} onCheckedChange={setVerifyWhatsapp} />
            </div>
          </div>
        );

      case 6:
        // Step 6: Ações pós-disparo
        return (
          <CampaignActionBuilder
            items={campaignActions}
            onItemsChange={setCampaignActions}
          />
        );

      case 7:
        // Step 7: Agendamento
        return (
          <div className="space-y-4">
            <div className="flex gap-4">
              <Button
                variant={scheduleType === 'now' ? 'default' : 'outline'}
                onClick={() => setScheduleType('now')}
                className="flex-1"
              >
                <Check className="h-4 w-4 mr-2" />
                Manter como rascunho
              </Button>
              <Button
                variant={scheduleType === 'scheduled' ? 'default' : 'outline'}
                onClick={() => setScheduleType('scheduled')}
                className="flex-1"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Agendar
              </Button>
            </div>

            {scheduleType === 'scheduled' && (
              <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                <div className="space-y-2">
                  <Label>Data</Label>
                  <Input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Horário</Label>
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // ---------------------------------------------------------------------------
  // Renderização Principal
  // ---------------------------------------------------------------------------
  
  const currentStep = STEPS[step - 1];
  const StepIcon = currentStep.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] h-[90vh] p-0 overflow-hidden gap-0 flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-full min-h-0 flex-1">
          {/* ============================================================ */}
          {/* SIDEBAR - Navegação de Steps + Resumo */}
          {/* ============================================================ */}
          <aside 
            className="hidden md:flex flex-col relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)'
            }}
          >
            {/* Elementos decorativos de fundo */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            </div>

            {/* Header da Sidebar */}
            <div className="relative z-10 p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Megaphone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold text-white">Editar Campanha</h2>
                  <p className="text-xs text-white/60">
                    {step} de {totalSteps} etapas
                  </p>
                </div>
              </div>
            </div>

            {/* Lista de Steps */}
            <nav className="relative z-10 flex-1 p-4 space-y-1 overflow-y-auto">
              {STEPS.map((s) => {
                const isActive = step === s.id;
                const isCompleted = step > s.id;
                const Icon = s.icon;

                return (
                  <button
                    key={s.id}
                    onClick={() => {
                      if (s.id <= step) setStep(s.id);
                    }}
                    disabled={s.id > step}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all",
                      "hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed",
                      isActive && "bg-white/10 border border-white/20",
                      !isActive && !isCompleted && "text-white/50"
                    )}
                  >
                    {/* Círculo indicador */}
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                      "border-2 transition-colors",
                      isCompleted && "bg-green-500 border-green-500 text-white",
                      isActive && "bg-white border-white text-primary",
                      !isActive && !isCompleted && "border-white/30 text-white/50"
                    )}>
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </div>

                    {/* Texto do step */}
                    <div className="flex-1 min-w-0">
                      <span className={cn(
                        "font-medium text-sm block truncate",
                        isActive && "text-white",
                        isCompleted && "text-white/70",
                        !isActive && !isCompleted && "text-white/50"
                      )}>
                        {s.label}
                      </span>
                    </div>

                    {/* Badge de contagem (quando aplicável) */}
                    {isCompleted && s.id === 2 && selectedInboxes.length > 0 && (
                      <Badge className="text-xs bg-white/20 text-white border-0 hover:bg-white/20">
                        {selectedInboxes.length}
                      </Badge>
                    )}
                    {isCompleted && s.id === 3 && recipientCount > 0 && (
                      <Badge className="text-xs bg-white/20 text-white border-0 hover:bg-white/20">
                        {recipientCount}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Resumo em tempo real */}
            <div className="relative z-10 p-4 border-t border-white/10 bg-white/5">
              <h3 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">
                Resumo
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Inboxes</span>
                  <span className="font-medium text-white">{selectedInboxes.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Destinatários</span>
                  <span className="font-medium text-white">{recipientCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Mensagens</span>
                  <span className="font-medium text-white">
                    {channelType === 'whatsapp_oficial' 
                      ? (selectedTemplate ? '1 template' : '0') 
                      : `${messageGroups.reduce((sum, g) => sum + g.items.length, 0)} (${messageGroups.length} ${messageGroups.length === 1 ? 'grupo' : 'grupos'})`}
                  </span>
                </div>
              </div>
            </div>
          </aside>

          {/* ============================================================ */}
          {/* CONTEÚDO PRINCIPAL */}
          {/* ============================================================ */}
          <main className="flex flex-col h-full min-h-0">
            {/* Header do Step */}
            <DialogHeader className="flex-shrink-0 px-6 py-5 border-b bg-background">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <StepIcon className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-lg font-semibold">
                    {currentStep.label}
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    {currentStep.description}
                  </DialogDescription>
                </div>
                {/* Badge mobile mostrando progresso */}
                <Badge variant="outline" className="md:hidden">
                  {step}/{totalSteps}
                </Badge>
              </div>

              {/* Stepper mobile */}
              <div className="flex items-center gap-1.5 mt-4 md:hidden">
                {STEPS.map((s) => (
                  <div
                    key={s.id}
                    className={cn(
                      "h-1.5 flex-1 rounded-full transition-colors",
                      step > s.id && "bg-green-500",
                      step === s.id && "bg-primary",
                      step < s.id && "bg-muted"
                    )}
                  />
                ))}
              </div>
            </DialogHeader>

            {/* Área de Conteúdo Scrollável */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="px-6 py-5">
                {renderStepContent()}
              </div>
            </div>

            {/* Footer com Navegação - sempre visível */}
            <div className="flex-shrink-0 px-6 py-4 border-t bg-background flex items-center justify-between sticky bottom-0">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={step === 1}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Voltar
              </Button>

              <span className="text-sm text-muted-foreground hidden sm:block">
                Etapa {step} de {totalSteps}
              </span>

              {step < totalSteps ? (
                <Button
                  onClick={handleNext}
                  disabled={!isStepValid()}
                  className="gap-1"
                >
                  Próximo
                  <ChevronRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    // Camada extra de segurança: validar limite de destinatários antes de salvar
                    if (recipientCount > MAX_CAMPAIGN_RECIPIENTS) {
                      sonnerToast.error(`Limite máximo de ${MAX_CAMPAIGN_RECIPIENTS.toLocaleString('pt-BR')} contatos por campanha. Para realizar disparos maiores, entre em contato com o comercial UaizyChat.`);
                      return;
                    }
                    updateMutation.mutate();
                  }}
                  disabled={!isStepValid() || updateMutation.isPending}
                  className="gap-2"
                >
                  {updateMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Salvar Alterações
                    </>
                  )}
                </Button>
              )}
            </div>
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
